import { Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { PROJECT_WORK_PERM } from '../../permissions'
import { fmtSize } from '../../taskFilters'
import type { Project } from '../../types'
import { FileList, UploadButton } from '../FileList'

const SCOPES = ['all', 'project', 'tasks'] as const
type Scope = (typeof SCOPES)[number]

export function FilesTab({ project }: { project: Project }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const [scope, setScope] = useState<Scope>('all')
  const [q, setQ] = useState('')
  const term = q.trim().toLocaleLowerCase('tr')
  const files = project.files.filter((f) => (scope === 'all' || (scope === 'project' ? !f.taskId : !!f.taskId)) && (!term || f.name.toLocaleLowerCase('tr').includes(term)))
  const total = project.files.reduce((sum, f) => sum + f.size, 0)

  return (
    <section className="space-y-4 rounded-xl border bg-card p-4 sm:p-5 shadow-card">
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={scope} onValueChange={(v) => setScope(v as Scope)}>
          <TabsList size="sm">
            {SCOPES.map((s) => (
              <TabsTrigger key={s} value={s}>
                {t(`projects.files.scope.${s}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative min-w-40 flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('projects.files.search')} aria-label={t('projects.files.search')} className="h-8 pl-8" />
        </div>
        <span className="text-xs text-muted-foreground">{t('projects.files.summary', { count: project.files.length, size: fmtSize(total) })}</span>
        {can(PROJECT_WORK_PERM) ? (
          <span className="ml-auto">
            <UploadButton project={project} taskId={null} />
          </span>
        ) : null}
      </div>
      <FileList project={project} files={files} canWrite={can(PROJECT_WORK_PERM)} layout="grid" showTask />
    </section>
  )
}
